import React, { useEffect, useRef, useState, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLoaderData } from "react-router-dom";
import {
  DocumentCategory,
  groupDocuments,
} from "../../utils/helpers/groupDocuments";
import SidebarAddDocument from "../../components/sidebarAddDocument/SidebarAddDocument";
import ModalPdfDocuments from "../../components/modalPdfDocuments/ModalPdfDocuments";
import { IDocument } from "../../types/documents";
import { useSearchParams } from "react-router-dom";
import { documentsService } from "../../api/documentsService/documentsService";
import { toast } from "react-toastify";
import { base64ToBlob } from "../../utils/helpers/base64ToBlob";
import DocumentCard from "../../components/documentCard/DocumentCard";
import Modal from "../../components/ui/modal/Modal";
import DocumentsTable from "./DocumentsTable";
import { ReactComponent as ChevronUpIcon } from "../../assets/images/icons/chevron-up.svg";
import { ReactComponent as ChevronDownIcon } from "../../assets/images/icons/chevron-down.svg";

const Documents: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const { documentsApi } = useLoaderData() as { documentsApi: IDocument[] };

  const [documents, setDocuments] = useState<IDocument[]>([]);
  const [groupedDocuments, setGroupedDocuments] = useState<DocumentCategory[]>(
    []
  );
  const [visualizationType, setVisualizationType] = useState<"grid" | "table">(
    "grid"
  );

  // Sidebar
  const [isOpenSidebarDocuments, setIsOpenSidebarDocuments] =
    useState<boolean>(false);
  const [activeSidebarDocument, setActiveSidebarDocument] =
    useState<null | IDocument>(null);
  const openCloseSidebarDocuments = (document: IDocument | null = null) => {
    if (isOpenSidebarDocuments === true) {
      setActiveSidebarDocument(null);
      setIsOpenSidebarDocuments(false);
    } else {
      setActiveSidebarDocument(document);
      setIsOpenSidebarDocuments(true);
    }
  };

  // Modal PDF
  const [isOpenModalPdfDocuments, setIsOpenModalPdfDocuments] =
    useState<boolean>(false);
  const [activeModalPdfDocument, setActiveModalPdfDocument] =
    useState<IDocument | null>(null);
  const openCloseModalPdfDocuments = (document: IDocument | null = null) => {
    if (isOpenModalPdfDocuments === true) {
      setActiveModalPdfDocument(null);
      setIsOpenModalPdfDocuments(false);
    } else {
      setActiveModalPdfDocument(document);
      setIsOpenModalPdfDocuments(true);
    }
  };

  // Modal sub documents
  const [isOpenSubDocumentsModal, setIsOpenSubDocumentsModal] = useState(false);
  const [activeModalDocument, setactiveModalDocument] =
    useState<IDocument | null>(null);

  const canGo = useRef(true);
  useEffect(() => {
    if (canGo.current === true && documentsApi.length > 0) {
      canGo.current = false;

      setDocuments(documentsApi);
      setGroupedDocuments(groupDocuments(documentsApi));

      const isInitialFilterPresent = !!searchParams.get("filter");
      if (isInitialFilterPresent) {
        setCurrentFilter(searchParams.get("filter"));
      }
    }
  }, []);

  useEffect(() => {
    setGroupedDocuments(groupDocuments(documents));
  }, [documents]);


  const openCloseDocumentGroup = (index: number) => () => {
    const groupedDocumentsCopy = [...groupedDocuments];
    groupedDocumentsCopy[index].open = !groupedDocumentsCopy[index].open;
    setGroupedDocuments(groupedDocumentsCopy);
  };

  const [loadingDownloadTemplate, setLoadingDownloadTemplate] = useState(false);
  const downloadTemplate = async (id: string) => {
    if (!!id && loadingDownloadTemplate === false) {
      setLoadingDownloadTemplate(true);
      const getTemplateRes = await documentsService.getTemplate(id);

      if (getTemplateRes.success) {
        let docType: null | "png" | "jpg" | "docx" | "pdf" = null;
        if (getTemplateRes.responseBody.fileName.includes(".pdf")) {
          docType = "pdf";
        } else if (getTemplateRes.responseBody.fileName.includes(".docx")) {
          docType = "docx";
        } else if (getTemplateRes.responseBody.fileName.includes(".png")) {
          docType = "png";
        } else if (getTemplateRes.responseBody.fileName.includes(".jpg")) {
          docType = "jpg";
        }

        const blobObj = base64ToBlob(getTemplateRes.responseBody.file, docType);
        if (blobObj.success === true && blobObj.file !== null) {
          const url = URL.createObjectURL(blobObj.file);
          window.open(url, "_blank");
        }
      } else {
        toast.error(t("errors.errorDownloadTemplate"));
      }

      setLoadingDownloadTemplate(false);
    }
  };

  const openCloseSubDocumentsModal = (document: IDocument) => {
    if (isOpenSubDocumentsModal === true) {
      setactiveModalDocument(null);
    } else {
      setactiveModalDocument(document);
    }
    setIsOpenSubDocumentsModal(!isOpenSubDocumentsModal);
  };

  const getRowClass = ({ data }: any) => {
    return hasRedBg(data) ? "row-grid-red" : "row-grid-white";
  };

  const filterDropdownRef = useRef<null | HTMLDivElement>(null);
  const [isOpenFilters, setIsOpenFilters] = useState<boolean>(false);
  const [currentFilter, setCurrentFilter] = useState<null | string>(null);
  const openFilters = () => {
    if (!isOpenFilters) {
      setIsOpenFilters(true);
    }
  };

  useEffect(() => {
    const listener = (e: any) => {
      if (
        !!filterDropdownRef.current &&
        !filterDropdownRef.current.contains(e.target) &&
        !isOpenFilters
      ) {
        setIsOpenFilters(false);
      }
    };
    document.addEventListener("click", listener);
    return () => {
      document.removeEventListener("click", listener);
    };
  }, []);

  const groupDocumentsCategories = useMemo(() => {
    if (!documents || documents.length === 0) return [];

    // Creo un set per evitare duplicati
    const statusSet = new Set<string>();

    documents.forEach(doc => {
      statusSet.add(doc.status); // Aggiungo lo status del documento principale
      doc.subDocuments?.forEach(subDoc => {
        statusSet.add(subDoc.status); // Aggiungo lo status dei subDocuments
      });
    });

    // Converto il set in un array e lo ordino
    return Array.from(statusSet).sort().map(status => ({
      type: status,
    }));
  }, [documents]);


  const filterDocuments = useMemo(() => {
    return documents
      .map(doc => {

        // Se il documento ha subDocuments, filtro solo quelli con lo status selezionato
        const filteredSubDocuments = doc.subDocuments?.filter(subDoc => subDoc.status === currentFilter) || [];

        // Se il documento principale ha lo status filtrato, mostro document con tutti i suoi subDocuments
        if (currentFilter !== null && doc.status === currentFilter) {
          if (filteredSubDocuments.length > 0) // Se il documento ha subDocuments con lo status selezionato, lo mostro con solo quei subDocuments
            return {
              ...doc,
              subDocuments: filteredSubDocuments, // Mantengo solo i subDocuments con lo status filtrato
            };
          else // Se il documento non ha subDocuments, lo mostro così com'è
            return doc;
        }

        // Se ci sono subDocuments con lo status selezionato, restituisco il documento con solo quei subDocuments
        if (filteredSubDocuments.length > 0) {
          return {
            ...doc,
            subDocuments: filteredSubDocuments, // Mantengo solo i subDocuments con lo status filtrato
          };
        }

        // Se il documento non ha lo status selezionato e non ha subDocuments con lo status selezionato, lo escludo
        return null;
      })
      .filter((doc): doc is IDocument => doc !== null) // Rimuovo gli elementi nulli
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [documents, currentFilter]);


  const flatDocuments = useMemo(() => {
    if (currentFilter === null) {
      // Se il filtro è "Tutti i documenti", mostriamo tutto come prima
      return documents.flatMap(doc => [doc, ...(doc.subDocuments || [])])
        .filter((doc): doc is IDocument => doc !== null);
    }

    return documents.flatMap(doc => {
      // Se il documento ha lo status selezionato, lo includiamo con tutti i suoi subDocuments
      if (doc.status === currentFilter) {
        return [doc, ...(doc.subDocuments || []).filter(subDoc => subDoc.status === currentFilter)];
      }

      // Se il documento non ha lo status selezionato, controlliamo se ha subDocuments con quello status
      const filteredSubDocuments = (doc.subDocuments || []).filter(subDoc => subDoc.status === currentFilter);

      // Se ci sono subDocuments con lo status selezionato, mostriamo solo loro (non il documento principale)
      if (filteredSubDocuments.length > 0) {
        return filteredSubDocuments;
      }

      // Se né il documento né i suoi subDocuments hanno lo status selezionato, escludiamo tutto
      return [];
    });
  }, [documents, currentFilter]);



  const changeFilter = (value: null | string) => () => {
    setCurrentFilter(value);
    setIsOpenFilters(false);
  };

  const hasRedBg: (document: IDocument) => boolean = (document) => {
    // Lo sfondo rosso si applica se l'azione è "caricamento obbligatorio" (3) o 'carica template' (4)
    return document.action === 3 || document.action === 4;
  };

  const renderModalContent = () => {
    if (!activeModalDocument) return null;

    return (
      <>
        <div className="hidden lg:block">
          <DocumentsTable
            documents={activeModalDocument?.subDocuments || []}
            getRowClass={getRowClass}
            hiddenColumns={["documentType"]}
            openCloseModalPdfDocuments={openCloseModalPdfDocuments}
            openCloseSidebarDocuments={openCloseSidebarDocuments}
          />
        </div>
        <div className="flex flex-col gap-y-6 lg:hidden">
          {activeModalDocument?.subDocuments?.map((document, i) => {
            return (
              <DocumentCard
                key={document.name + i}
                document={document}
                id={i}
                hasRedBg={hasRedBg(document)}
                downloadTemplate={downloadTemplate}
                openCloseModalPdfDocuments={openCloseModalPdfDocuments}
                openCloseSidebarDocuments={openCloseSidebarDocuments}
                openCloseSubDocumentsModal={openCloseSubDocumentsModal}
                hasTemplate={activeModalDocument.hasTemplate}
              />
            );
          })}
        </div>
      </>
    );
  };

  return (
    <div className="p-6 md:p-8">
      <SidebarAddDocument
        callbackCloseSidebar={() => openCloseSidebarDocuments()}
        isOpen={isOpenSidebarDocuments && activeSidebarDocument !== null}
        activeDocument={activeSidebarDocument}
        callbackUpdateDocuments={(docs) => {
          setDocuments(docs);

          // 'groupedDocuments' viene aggiornato nello useEffect
          if (activeModalDocument) {
            // Aggiorna 'activeModalDocument' con i dati più recenti
            const updatedDocument = docs.find(
              (doc: any) => (
                doc.id === activeModalDocument.id &&
                doc.name === activeModalDocument.name &&
                doc.documentType === activeModalDocument.documentType
              )
            );

            if (updatedDocument) {
              setactiveModalDocument(updatedDocument);
            } else {
              // Verifica se 'activeModalDocument' è un subDocument
              let found = false;
              for (const doc of docs) {
                if (doc.subDocuments) {
                  const subDoc = doc.subDocuments.find(
                    (sub: any) => sub.id === activeModalDocument.id
                  );
                  if (subDoc) {
                    setactiveModalDocument(subDoc);
                    found = true;
                    break;
                  }
                }
              }
            }
          }
        }}

      />

      {isOpenModalPdfDocuments && activeModalPdfDocument !== null && (
        <ModalPdfDocuments
          document={activeModalPdfDocument}
          callbackCloseModal={() => openCloseModalPdfDocuments()}
        />
      )}

      <div className="md:flex items-start mb-6 md:mb-[30px]">
        <h1 className="mb-4 md:mb-0 text-[32px] font-bold leading-[37.5px]">
          {t("screens.documents.heading")}
        </h1>
      </div>

      {!!groupedDocuments && (
        <div className="flex items-center justify-between mb-[26px]">
          {/* Grid or Table buttons */}
          <div className="hidden md:flex items-center">
            <div
              className={
                "mr-[10px] cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" +
                (visualizationType === "grid" ? " bg-red" : "")
              }
              onClick={() => setVisualizationType("grid")}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 0H6V6H0V0ZM8 0H14V6H8V0ZM0 8H6V14H0V8Z"
                  fill={visualizationType === "grid" ? "#FFFFFF" : "#6F6F6F"}
                />
                <path
                  d="M14 8H8V14H14V8Z"
                  fill={visualizationType === "grid" ? "#FFFFFF" : "#6F6F6F"}
                />
              </svg>
            </div>

            <div
              className={
                "cursor-pointer h-6 w-6 rounded-[4px] flex items-center justify-center" +
                (visualizationType === "table" ? " bg-red" : "")
              }
              onClick={() => setVisualizationType("table")}
            >
              <svg
                width="16"
                height="10"
                viewBox="0 0 16 10"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M0 0H16V2H0V0ZM0 4H16V6H0V4ZM0 8H16V10H0V8Z"
                  fill={visualizationType === "table" ? "#FFFFFF" : "#6F6F6F"}
                />
              </svg>
            </div>
          </div>

          {/* FILTERS */}
          {((currentFilter === null && groupedDocuments.length > 0) ||
            (currentFilter !== null && filterDocuments.length > 0)) && (
              <div
                ref={filterDropdownRef}
                onClick={openFilters}
                className="relative flex items-center cursor-pointer select-none"
              >
                <div className="mr-[10px]">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M20 3H4C3.73478 3 3.48043 3.10536 3.29289 3.29289C3.10536 3.48043 3 3.73478 3 4V6.59C3 7.113 3.213 7.627 3.583 7.997L9 13.414V21C9.0002 21.1704 9.04387 21.3379 9.1269 21.4867C9.20992 21.6354 9.32955 21.7605 9.47444 21.8502C9.61934 21.9398 9.78471 21.9909 9.9549 21.9988C10.1251 22.0066 10.2945 21.9709 10.447 21.895L14.447 19.895C14.786 19.725 15 19.379 15 19V13.414L20.417 7.997C20.787 7.627 21 7.113 21 6.59V4C21 3.73478 20.8946 3.48043 20.7071 3.29289C20.5196 3.10536 20.2652 3 20 3Z"
                      fill="#CF0A2C"
                    />
                  </svg>
                </div>
                <div className="uppercase font-semibold text-sm leading-[17px]">
                  {currentFilter === null && (
                    <span>{t("screens.documents.filter")}</span>
                  )}
                  {currentFilter !== null && (
                    <span className="uppercase font-semibold text-sm leading-[17px]">
                      <span className="text-red">
                        {t("screens.documents.filter")}:
                      </span>
                      <span className="ml-1">{currentFilter}</span>
                    </span>
                  )}
                </div>

                {isOpenFilters && (
                  <div className="absolute z-10 w-max right-[100%] translate-x-[100%] md:right-0 md:translate-x-0 top-[calc(100%+10px)] bg-white border border-red rounded-[8px] p-4">
                    <div
                      onClick={changeFilter(null)}
                      className={
                        "text-sm leading-[17px] mb-4 flex items-center" +
                        (currentFilter === null ? " font-bold" : "")
                      }
                    >
                      {currentFilter === null && (
                        <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                      )}
                      <span>{t("screens.documents.allDocuments")}</span>
                    </div>
                    {groupDocumentsCategories.map((documentCategory, j) => {
                      return (
                        <div
                          onClick={changeFilter(documentCategory.type)}
                          key={j}
                          className={
                            "text-sm leading-[17px] flex items-center" +
                            (j === groupDocumentsCategories.length - 1
                              ? ""
                              : " mb-4") +
                            (currentFilter === documentCategory.type
                              ? " font-bold"
                              : "")
                          }
                        >
                          {currentFilter === documentCategory.type && (
                            <span className="block h-[6px] w-[6px] rounded-full bg-red mr-1"></span>
                          )}
                          <span>{documentCategory.type}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
        </div>
      )}

      <div>
        {visualizationType === "grid" && (
          <>
            {/* WHEN NOT FILTERED */}
            {currentFilter === null && (
              <div>
                {groupedDocuments.map((documentGroup, index) => {
                  return (
                    <div key={index}>
                      <div
                        onClick={openCloseDocumentGroup(index)}
                        className={
                          "flex justify-between items-center select-none cursor-pointer pb-2 border-b" +
                          (documentGroup.open === true ? "" : " mb-6")
                        }
                      >
                        <div className="font-bold text-[24px] leading-[28px]">
                          {documentGroup.type}
                        </div>

                        <div>
                          {documentGroup.open === true && <ChevronUpIcon />}

                          {documentGroup.open === false && <ChevronDownIcon />}
                        </div>
                      </div>

                      {!!documentGroup.documents &&
                        documentGroup.documents.length > 0 &&
                        documentGroup.open === true && (
                          <div className="grid grid-cols-12 gap-6 py-6">
                            {documentGroup.documents.map((document, i) => {
                              return (
                                <DocumentCard
                                  key={document.name + index + i}
                                  document={document}
                                  id={i}
                                  hasRedBg={hasRedBg(document)}
                                  downloadTemplate={downloadTemplate}
                                  openCloseModalPdfDocuments={
                                    openCloseModalPdfDocuments
                                  }
                                  openCloseSidebarDocuments={
                                    openCloseSidebarDocuments
                                  }
                                  openCloseSubDocumentsModal={
                                    openCloseSubDocumentsModal
                                  }
                                  hasTemplate={document.hasTemplate}
                                />
                              );
                            })}
                          </div>
                        )}
                    </div>
                  );
                })}

                {groupedDocuments.length === 0 && (
                  <div>{t("screens.documents.emptyDocuments")}</div>
                )}
              </div>
            )}

            {/* WHEN IS FILTERED */}
            {currentFilter !== null && (
              <div className="grid grid-cols-12 gap-6 py-6">
                {filterDocuments.map((document, i) => {
                  return (
                    <DocumentCard
                      key={document.name + i}
                      document={document}
                      id={i}
                      hasRedBg={hasRedBg(document)}
                      downloadTemplate={downloadTemplate}
                      openCloseModalPdfDocuments={openCloseModalPdfDocuments}
                      openCloseSidebarDocuments={openCloseSidebarDocuments}
                      openCloseSubDocumentsModal={openCloseSubDocumentsModal}
                      hasTemplate={document.hasTemplate}
                    />
                  );
                })}

                {filterDocuments.length === 0 && (
                  <div>{t("screens.documents.emptyDocuments")}</div>
                )}
              </div>
            )}
          </>
        )}

        {visualizationType === "table" && (
          <DocumentsTable
            documents={flatDocuments}
            getRowClass={getRowClass}
            openCloseModalPdfDocuments={openCloseModalPdfDocuments}
            openCloseSidebarDocuments={openCloseSidebarDocuments}
            grid={true}
          />
        )}
      </div>

      <Modal
        state={[isOpenSubDocumentsModal, setIsOpenSubDocumentsModal]}
        title={activeModalDocument?.documentType}
        titleClassName="uppercase font-bold"
      //width= "1300px"
      >
        {renderModalContent()}
      </Modal>
    </div>
  );
};

export default Documents;
